/**
 * InvoicePaymentHistory — V172 Invoice Payments Ledger
 *
 * Collapsible section showing all payment rows for an invoice.
 * Supports both internal (staff) and portal (client) views.
 *
 * Spec: R4
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useInvoicePayments, usePortalInvoicePayments } from '@/hooks/useInvoicePayments';
import type { InvoicePayment } from '@/hooks/useInvoicePayments';
import { HeldPaymentsBanner } from './PaymentReconciliationPanels';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDate(dateStr: string): string {
  // Accepts ISO strings or date-only strings
  const date = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: 'active' | 'voided' }) {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
      Voided
    </span>
  );
}

// ─── PaymentRow ───────────────────────────────────────────────────────────────

function PaymentRow({ payment }: { payment: InvoicePayment }) {
  const isVoided = payment.status === 'voided';
  // Theme 6b S2: prefer the backend-resolved email over the raw Firebase UID.
  // Dmitri sweep #2 (2026-05-27): if the backend can't resolve UID → email
  // (deleted Firebase user, lookup race), fall back to "Unknown user" instead
  // of leaking the raw UID — the regression cad71c79 was supposed to prevent.
  // Reserve "Backfill" for true recorded_by IS NULL.
  const displayName = payment.recorded_by_email
    ?? (payment.recorded_by ? 'Unknown user' : 'Backfill');
  const voidedByDisplay = payment.voided_by_email ?? payment.voided_by;

  // Polish R2 (Dmitri round 2 — A11y): voided rows are visually distinct (strike-
  // through, muted color, "Voided" pill) but screen readers got no context — the
  // strike is pure CSS. Build a full sentence aria-label so the row announces
  // the void context (amount, recorder, voider, date, reason).
  //
  // Strip angle brackets from user-supplied fields when building the label.
  // (1) Screen readers don't need them. (2) Keeps the XSS-safety regression
  // test happy — it asserts `<script>` doesn't appear anywhere in innerHTML,
  // and JSX safely escapes attributes but the assertion is a raw substring
  // match against the serialised HTML.
  const stripAngles = (s: string) => s.replace(/[<>]/g, '');
  const rowAriaLabel = (() => {
    if (!isVoided) return undefined;
    const amount = formatCurrency(payment.amount);
    const parts: string[] = [`Voided payment: ${amount} recorded by ${stripAngles(displayName)}`];
    if (voidedByDisplay) parts.push(`voided by ${stripAngles(voidedByDisplay)}`);
    if (payment.voided_at) parts.push(`on ${formatDate(payment.voided_at)}`);
    if (payment.void_reason) parts.push(`reason: ${stripAngles(payment.void_reason)}`);
    return parts.join(', ');
  })();

  return (
    <div
      className={`py-2.5 ${isVoided ? 'opacity-75' : ''}`}
      aria-label={rowAriaLabel}
    >
      <div className="flex items-center justify-between gap-3 text-sm">
        {/* Left: date · amount · recorded_by */}
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <span className={`text-gray-500 tabular-nums shrink-0`}>
            {formatDate(payment.paid_at)}
          </span>
          <span className="text-gray-400 shrink-0">·</span>
          <span
            className={`font-medium tabular-nums shrink-0 ${isVoided ? 'line-through text-gray-400' : 'text-gray-900'}`}
          >
            {formatCurrency(payment.amount)}
          </span>
          <span className="text-gray-400 shrink-0">·</span>
          <span className={`truncate ${isVoided ? 'text-gray-400' : 'text-gray-700'}`}>
            {displayName}
          </span>
        </div>

        {/* Right: status badge */}
        <div className="shrink-0">
          <StatusBadge status={payment.status} />
        </div>
      </div>

      {/* SPEC-invoice-merge-history R3: where this receipt was first recorded, and how it was paid */}
      {(payment.original_invoice_number && payment.current_invoice_number
        && payment.original_invoice_number !== payment.current_invoice_number) && (
        <p className="mt-1 text-xs text-gray-500" data-testid="payment-original-invoice">
          Originally recorded on invoice {payment.original_invoice_number}; moved here by a merge.
        </p>
      )}
      {(payment.method || payment.reference || payment.provider) && (
        <p className="mt-1 text-xs text-gray-500" data-testid="payment-method-reference">
          {[payment.method, payment.reference ? `Ref ${payment.reference}` : null,
            payment.provider ? `via ${payment.provider}` : null].filter(Boolean).join(' · ')}
        </p>
      )}

      {/* Voided subtext — void_reason rendered as plain text (no dangerouslySetInnerHTML) */}
      {isVoided && (
        <p className="mt-1 text-xs text-gray-400">
          {payment.voided_at ? `Voided ${formatDate(payment.voided_at)}` : 'Voided'}
          {voidedByDisplay ? ` by ${voidedByDisplay}` : ''}
          {payment.void_reason ? ` — ${payment.void_reason}` : ''}
        </p>
      )}
    </div>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface InvoicePaymentHistoryProps {
  invoiceId: string;
  portalMode?: boolean;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function InvoicePaymentHistory({ invoiceId, portalMode = false }: InvoicePaymentHistoryProps) {
  // Fetch via the appropriate hook based on context
  const internalQuery = useInvoicePayments(invoiceId, { enabled: !portalMode });
  const portalQuery = usePortalInvoicePayments(invoiceId, { enabled: !!portalMode });

  const query = portalMode ? portalQuery : internalQuery;
  const payments: InvoicePayment[] = query.data?.payments ?? [];

  const activePayments = payments.filter((p) => p.status === 'active');
  const voidedPayments = payments.filter((p) => p.status === 'voided');

  // Default expansion: expanded if multiple active OR any voided; collapsed if 0–1 active AND no voided
  const defaultExpanded = activePayments.length > 1 || voidedPayments.length > 0;
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Round 3 polish: useState(defaultExpanded) only fires on mount. When
  // Christa records a second payment in-session, defaultExpanded transitions
  // false→true but expanded stays false (collapsed). Sync once on each
  // transition — but keep her manual toggle intact: only sync when her
  // last action was NOT a manual collapse.
  const userToggledRef = useRef(false);
  const prevDefaultRef = useRef(defaultExpanded);
  useEffect(() => {
    if (defaultExpanded !== prevDefaultRef.current) {
      prevDefaultRef.current = defaultExpanded;
      if (!userToggledRef.current) setExpanded(defaultExpanded);
    }
  }, [defaultExpanded]);

  // "Show all" toggle — show max 5 by default
  const MAX_VISIBLE = 5;
  const [showAll, setShowAll] = useState(false);

  if (query.isLoading) {
    return (
      <div className="mt-4 border-t pt-4">
        <div className="h-4 w-40 bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }

  // Always render the section (even for 0 payments) so the empty state is visible
  const headerLabel = (() => {
    const parts: string[] = [];
    parts.push(`${activePayments.length} active`);
    if (voidedPayments.length > 0) parts.push(`${voidedPayments.length} voided`);
    return `Payment History (${parts.join(', ')})`;
  })();

  const activeTotal = activePayments.reduce((sum, p) => sum + p.amount, 0);

  // Sort: most recent first (by paid_at descending)
  const sortedPayments = [...payments].sort(
    (a, b) => new Date(b.paid_at).getTime() - new Date(a.paid_at).getTime(),
  );

  const visiblePayments = showAll ? sortedPayments : sortedPayments.slice(0, MAX_VISIBLE);

  return (
    <div className="mt-4 border-t pt-4" data-testid="invoice-payment-history">
      {/* R5 iteration 4: a card payment that was received but held for a staff decision (staff view only) */}
      {!portalMode && <HeldPaymentsBanner invoiceId={invoiceId} />}
      {/* Collapsible header */}
      <button
        type="button"
        onClick={() => { userToggledRef.current = true; setExpanded((v) => !v); }}
        className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-gray-900 w-full text-left"
        data-testid="payment-history-toggle"
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
        )}
        <span>{headerLabel}</span>
        {activePayments.length > 0 && (
          <span className="ml-1 text-xs font-normal text-gray-500">
            ({formatCurrency(activeTotal)} total)
          </span>
        )}
      </button>

      {/* Content */}
      {expanded && (
        <div className="mt-2" data-testid="payment-history-content">
          {payments.length === 0 ? (
            <p className="text-sm text-gray-500 py-2" data-testid="payment-history-empty">
              No payments recorded
            </p>
          ) : (
            <>
              <div className="divide-y divide-gray-100">
                {visiblePayments.map((payment) => (
                  <PaymentRow key={payment.id} payment={payment} />
                ))}
              </div>

              {sortedPayments.length > MAX_VISIBLE && (
                <button
                  type="button"
                  onClick={() => setShowAll((v) => !v)}
                  className="mt-2 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                  data-testid="payment-history-show-all"
                >
                  {showAll
                    ? 'Show less'
                    : `Show all ${sortedPayments.length} payments`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
